import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { X } from "lucide-react";
import DocumentUploader from "./DocumentUploader";
import ManualPicker from "./ManualPicker";

export default function ScaffoldDialog({ open, initial, onClose, onSaved, kind = "scaffalatura", initialClientId = "" }) {
  const [form, setForm] = useState({
    reference: "",
    client_id: "",
    address: "",
    notes: "",
    planimetria: [],
    documents: [],
    manual_ids: [],
  });
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/clients");
        setClients(data);
      } catch {
        /* admin only */
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        reference: initial.reference || "",
        client_id: initial.client_id || "",
        address: initial.address || "",
        notes: initial.notes || "",
        planimetria: (initial.planimetria || []).map((p) =>
          typeof p === "string" ? { path: p } : p
        ),
        documents: (initial.documents || []).map((p) =>
          typeof p === "string" ? { path: p } : p
        ),
        manual_ids: initial.manual_ids || [],
      });
    } else {
      setForm({ reference: "", client_id: initialClientId || "", address: "", notes: "", planimetria: [], documents: [], manual_ids: [] });
    }
    setError("");
  }, [open, initial, initialClientId]);

  if (!open) return null;

  const isEdit = Boolean(initial);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.client_id) {
      setError("Seleziona un cliente");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, kind };
      if (!payload.notes) payload.notes = "";
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/scaffolds/${initial.id}`, payload));
      } else {
        ({ data } = await api.post("/scaffolds", payload));
      }
      setLoading(false);
      onSaved(data);
    } catch (err) {
      setLoading(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="scaffold-dialog"
    >
      <div className="bru-card w-full max-w-xl p-6 mt-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="tag-label">{isEdit ? "MODIFICA" : "NUOVA · REGISTRA"}</div>
            <h3 className="text-2xl font-bold" >
              {isEdit
                ? (kind === "lineavita" ? "Modifica Anticaduta" : "Modifica Scaffalatura")
                : (kind === "lineavita" ? "Nuova Anticaduta" : "Nuova Scaffalatura")}
            </h3>
          </div>
          <button onClick={onClose} className="bru-btn bru-btn-ghost !px-2 !py-1" data-testid="close-scaffold-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!isEdit && clients.length === 0 && (
          <div className="border-[1.5px] border-black bg-[#ffcc00] text-black px-3 py-2 text-sm mb-4">
            Nessun cliente registrato. Crea prima un cliente.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="bru-label">Riferimento *</label>
            <input
              required
              className="bru-input"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Scaffale A1 · Magazzino Nord"
              data-testid="scaffold-reference-input"
            />
          </div>
          <div>
            <label className="bru-label">Cliente *</label>
            <select
              required
              className="bru-input"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              data-testid="scaffold-client-select"
            >
              <option value="">— Seleziona cliente —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="bru-label">Indirizzo *</label>
            <input
              required
              className="bru-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Via Roma 10, 20100 Milano"
              data-testid="scaffold-address-input"
            />
          </div>
          <div>
            <label className="bru-label">Note</label>
            <textarea
              className="bru-input"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="scaffold-notes-input"
            />
          </div>
          <div>
            <label className="bru-label">Planimetria (immagini, PDF o DWG/DXF)</label>
            <DocumentUploader
              value={form.planimetria}
              onChange={(arr) => setForm({ ...form, planimetria: arr })}
              max={10}
              allowCad
            />
          </div>
          <div>
            <label className="bru-label">Documenti PDF</label>
            <DocumentUploader
              value={form.documents}
              onChange={(arr) => setForm({ ...form, documents: arr })}
              max={50}
            />
          </div>
          <div>
            <label className="bru-label">Manuali d'uso</label>
            <ManualPicker kind="scaffold" value={form.manual_ids} onChange={(ids) => setForm({ ...form, manual_ids: ids })} />
          </div>
          {error && (
            <div className="border-[1.5px] border-black bg-[#ff3b30] text-white px-3 py-2 text-sm" data-testid="scaffold-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary" data-testid="cancel-scaffold-button">
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || (!isEdit && clients.length === 0)}
              className="bru-btn bru-btn-primary disabled:opacity-60"
              data-testid="submit-scaffold-button"
            >
              {loading ? "SALVATAGGIO…" : isEdit ? "AGGIORNA" : "REGISTRA"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
